import React, {useMemo, useState} from "react";
import {Route} from "@/routing/Router";
import {useQuery} from "@tanstack/react-query";
import {FormattedMessage} from "react-intl";
import {Spinner} from "peggirkit";
import {api} from "@/data/api";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {ADMIN_MENU_QUERY_KEY} from "@/data/entities/menuEditor";
import {OptionGroup} from "@/data/entities/option";
import OptionGroupsOverviewHeading from "@widgets/menu/optionGroups/heading/OptionGroupsOverviewHeading";
import OptionGroupsList from "@widgets/menu/optionGroups/nav/OptionGroupsList";
import OptionGroupSection from "@widgets/menu/optionGroups/group/OptionGroupSection";
import RemoveOptionModal, {RemoveOptionState} from "@widgets/menu/optionGroups/remove/RemoveOptionModal";
import {
    createOptionGroupsLookup,
    OptionGroupsLookup,
    sortOptionGroups,
} from "@widgets/menu/optionGroups/optionGroupsLookup";

type Props = {
    route: Route,
};

const OptionGroupsOverview = ({route}: Props) => {
    const {dashboardLocation} = useRestaurant();
    const [removeOption, setRemoveOption] = useState<RemoveOptionState>({
        show: false,
        group: null,
        option: null,
        otherGroupCount: 0,
    });
    const {data: menu, isFetching} = useQuery({
        queryKey: [ADMIN_MENU_QUERY_KEY, dashboardLocation.id],
        queryFn: () => api.menuEditor.getMenu(dashboardLocation.id),
    });
    const optionGroups: OptionGroup[] = useMemo(
        () => menu === undefined ? [] : sortOptionGroups(menu.optionGroups),
        [menu]);
    const lookup: OptionGroupsLookup | null = useMemo(
        () => menu === undefined ? null : createOptionGroupsLookup(menu),
        [menu]);

    return (
        <>
            <OptionGroupsOverviewHeading route={route}/>

            <div className={"mt-6 md:mt-12 w-full grid grid-cols-5 gap-6 md:gap-12 items-stretch"}>
                {
                    optionGroups.length > 0 &&
                    <nav className={"col-span-5 md:col-span-2 xl:col-span-1 self-start md:sticky md:top-6"}>
                        <OptionGroupsList
                            optionGroups={optionGroups}
                            isFetching={isFetching}
                        />
                    </nav>
                }

                <ol className={"col-span-5 md:col-span-3 xl:col-span-4 w-full flex flex-col gap-8 md:gap-16"}>
                    {
                        lookup === null
                            ? <li><Spinner size={"lg"}/></li>
                            : optionGroups.length === 0
                                ? (
                                    <li className={"text-sm text-gray-500 dark:text-slate-400"}>
                                        <FormattedMessage id={"noOptionGroups"}/>
                                    </li>
                                )
                                : optionGroups.map((group: OptionGroup) =>
                                    <OptionGroupSection
                                        key={group.id}
                                        group={group}
                                        lookup={lookup}
                                        disabled={isFetching}
                                        onRemoveOption={(option, otherGroupCount) => setRemoveOption({
                                            show: true,
                                            group,
                                            option,
                                            otherGroupCount,
                                        })}
                                    />
                                )
                    }
                </ol>
            </div>

            <RemoveOptionModal
                state={removeOption}
                setShow={(show: boolean) => setRemoveOption(prev => ({...prev, show}))}
            />
        </>
    );
};

export default OptionGroupsOverview;